import React, { useEffect, useState, Fragment, createElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ShareIcon, CheckIcon } from 'lucide-react';
import { fetchSanity, urlForSanityImage } from '../lib/sanity';
// Helper to convert a name like "Edith Yeung" to "edith-yeung"
const generateAuthorSlug = (name: string) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};
const MOCK_POST = {
  title: 'The AI Platform for the Software That Runs the World',
  author: 'Edith Yeung',
  publishedAt: '2026-05-20T12:00:00Z',
  imageUrl: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=2865&auto=format&fit=crop',
  body: `
    <p>The enterprise software landscape is undergoing a fundamental shift. For the past decade, we've seen the transition from on-premise to cloud, and from monolithic architectures to microservices. But the next wave of transformation is being driven by something entirely different: artificial intelligence.</p>
    <p>When we first met the team, it was immediately clear that they weren't just building another tool for developers. They were rethinking the entire paradigm of how software is constructed, deployed, and maintained in an AI-first world.</p>
    <h2>The Hair-on-Fire Problem</h2>
    <p>Every engineering leader we speak with is facing the same challenge: how do we integrate AI capabilities into our existing products without completely rewriting our stack? The current approach of bolting on LLM APIs to legacy systems is brittle, expensive, and hard to scale.</p>
    <blockquote>"The companies that win in the next decade won't be the ones with the best models, but the ones with the best infrastructure to deploy and iterate on those models."</blockquote>
    <h2>Why We Invested</h2>
    <ul>
      <li><strong>Developer Experience:</strong> The platform feels like magic. It takes tasks that used to require a team of ML engineers weeks to build and turns them into a few lines of code.</li>
      <li><strong>Enterprise Readiness:</strong> From day one, the architecture was designed with security, compliance, and scale in mind.</li>
    </ul>
  `
};
export const BlogPostPage = () => {
  const {
    slug
  } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isMock, setIsMock] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const getPost = async () => {
      try {
        const query = `*[_type == "post" && (slug.current == "${slug}" || _id == "${slug}")][0] {
          title,
          author,
          "publishedAt": coalesce(publishedAt, _createdAt),
          "imageUrl": mainImage.asset->url,
          body
        }`;
        const data = await fetchSanity(query);
        if (data) {
          setPost(data);
        } else {
          setPost(MOCK_POST);
          setIsMock(true);
        }
      } catch (err) {
        console.error('Failed to fetch post:', err);
        setPost(MOCK_POST);
        setIsMock(true);
      } finally {
        setLoading(false);
      }
    };
    if (slug) {
      getPost();
    }
  }, [slug]);
  const handleShare = async () => {
    const url = window.location.href;
    try {
      // Try native share first (will likely fail inside an iframe)
      if (navigator.share) {
        await navigator.share({
          title: post?.title || 'Race Capital Blog',
          url: url
        });
        return; // If successful, we're done
      }
    } catch (err) {
      console.log('Native share not supported or blocked, falling back to clipboard.');
    }
    // Fallback: Copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Ultimate fallback if clipboard API is blocked
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  if (loading) {
    return <div className="w-full min-h-screen bg-white pt-32 pb-32 flex justify-center items-center" data-id="element-140">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" data-id="element-141"></div>
      </div>;
  }
  if (!post) {
    return <div className="w-full min-h-screen bg-white pt-32 pb-32 flex flex-col items-center justify-center" data-id="element-142">
        <h1 className="text-3xl font-bold text-[#070707] mb-4" data-id="element-143">
          Post not found
        </h1>
        <Link to="/blog" className="text-red-600 hover:underline" data-id="element-144">
          Return to Blog
        </Link>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white pt-32 pb-32" data-id="element-145">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-146">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-12" data-id="element-147">
          {/* Back Button */}
          <Link to="/blog1" className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors uppercase tracking-wider mb-8 md:mb-0 group" data-id="element-148">
            <ArrowLeftIcon className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" data-id="element-149" />
            Back to Blog
          </Link>
        </div>

        {/* Title */}
        <div className="max-w-3xl mx-auto mb-16" data-id="element-150">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#070707] leading-[1.1] tracking-tight" data-id="element-151">
            {post.title || 'Untitled Post'}
          </h1>
        </div>

        {/* Featured Image */}
        {post.imageUrl && <div className="max-w-3xl mx-auto w-full aspect-[2/1] rounded-2xl overflow-hidden mb-20 bg-gray-100" data-id="element-152">
            <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" data-id="element-153" />
          </div>}

        {/* Content */}
        <div className="max-w-3xl mx-auto" data-id="element-154">
          <div className="flex items-end justify-between border-b border-gray-200 pb-8 mb-12" data-id="element-155">
            <div data-id="element-156">
              {post.author && <div className="mb-2 text-lg text-[#070707] font-semibold" data-id="element-157">
                  {post.author.split(',').map((authorName: string, index: number, array: string[]) => {
                const cleanName = authorName.trim();
                return <Fragment key={index} data-id="element-158">
                            <Link to={`/team/${generateAuthorSlug(cleanName)}`} className="hover:text-red-600 transition-colors" data-id="element-159">
                              {cleanName}
                            </Link>
                            {index < array.length - 1 && ', '}
                          </Fragment>;
              })}
                </div>}
              <div className="text-gray-500 font-medium" data-id="element-160">
                {post.publishedAt ? `Published on ${new Date(post.publishedAt).toLocaleDateString()}` : 'Recently published'}
              </div>
            </div>
            <div className="flex space-x-4 mb-1" data-id="element-161">
              <button onClick={handleShare} className={`flex items-center transition-colors font-medium text-sm uppercase tracking-wider ${copied ? 'text-green-600' : 'text-gray-400 hover:text-red-600'}`} data-id="element-162">
                {copied ? <>
                    <CheckIcon className="w-4 h-4 mr-2" data-id="element-163" />
                    Copied!
                  </> : <>
                    <ShareIcon className="w-4 h-4 mr-2" data-id="element-164" />
                    Share
                  </>}
              </button>
            </div>
          </div>

          <div className="prose prose-lg md:prose-xl max-w-none prose-headings:font-bold prose-headings:text-[#070707] prose-headings:tracking-tight prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-red-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-red-600 prose-blockquote:text-gray-900 prose-blockquote:font-medium prose-blockquote:italic prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-li:text-gray-700" data-id="element-165">
            {typeof post.body === 'string' ? <div dangerouslySetInnerHTML={{
            __html: post.body
          }} data-id="element-166" /> : Array.isArray(post.body) ?
          // Enhanced Portable Text Renderer for preview purposes
          <div data-id="element-167">
                {post.body.map((block: any, index: number) => {
              // Handle image blocks
              if (block._type === 'image') {
                const imageUrl = urlForSanityImage(block);
                if (!imageUrl) return null;
                return <figure key={index} className="my-12" data-id="element-168">
                        <img src={imageUrl} alt={block.alt || 'Blog post image'} className="w-full rounded-2xl bg-gray-100" data-id="element-169" />
                        {block.caption && <figcaption className="text-center text-sm text-gray-500 mt-3" data-id="element-170">
                            {block.caption}
                          </figcaption>}
                      </figure>;
              }
              // Handle standalone embed/link blocks that aren't standard text blocks
              if (block._type !== 'block' || !block.children) {
                const linkUrl = block.url || block.href;
                if (linkUrl) {
                  return <div key={index} className="my-6 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-171">
                          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-red-600 underline font-medium break-all" data-id="element-172">
                            {block.title || linkUrl}
                          </a>
                        </div>;
                }
                return null;
              }
              const isEmpty = block.children.every((child: any) => !child.text || child.text.trim() === '');
              if (isEmpty) {
                return <p key={index} className="min-h-[1.5em] m-0" data-id="element-173">
                        &nbsp;
                      </p>;
              }
              const childrenContent = block.children.map((child: any, cIndex: number) => {
                let content: React.ReactNode = child.text;
                // Handle soft line breaks (\n) inside text blocks
                if (typeof content === 'string' && content.includes('\n')) {
                  content = content.split('\n').map((line, i, arr) => <Fragment key={i} data-id="element-174">
                            {line}
                            {i < arr.length - 1 && <br data-id="element-175" />}
                          </Fragment>);
                }
                if (child.marks && child.marks.length > 0) {
                  child.marks.forEach((mark: string) => {
                    if (mark === 'strong') {
                      content = <strong key={mark} data-id="element-176">{content}</strong>;
                    } else if (mark === 'em') {
                      content = <em key={mark} data-id="element-177">{content}</em>;
                    } else {
                      // Check for complex marks like links in markDefs
                      const def = block.markDefs?.find((d: any) => d._key === mark);
                      if (def && (def._type === 'link' || def.href || def.url)) {
                        const href = def.href || def.url;
                        content = <a key={mark} href={href} target="_blank" rel="noopener noreferrer" className="text-red-600 underline decoration-red-600/30 hover:decoration-red-600 underline-offset-2 font-medium transition-all" data-id="element-178">
                                  {content}
                                </a>;
                      }
                    }
                  });
                }
                return <Fragment key={cIndex} data-id="element-179">{content}</Fragment>;
              });
              // Handle list items
              if (block.listItem === 'bullet') {
                return <li key={index} className="ml-6 list-disc my-2" data-id="element-180">
                        {childrenContent}
                      </li>;
              }
              if (block.listItem === 'number') {
                return <li key={index} className="ml-6 list-decimal my-2" data-id="element-181">
                        {childrenContent}
                      </li>;
              }
              // Handle basic styles
              if (block.style === 'h1') return <h1 key={index} className="text-4xl md:text-5xl font-bold mt-16 mb-8 text-[#070707] tracking-tight" data-id="element-182">
                        {childrenContent}
                      </h1>;
              if (block.style === 'h2') return <h2 key={index} className="text-3xl md:text-4xl font-bold mt-14 mb-6 text-[#070707] tracking-tight" data-id="element-183">
                        {childrenContent}
                      </h2>;
              if (block.style === 'h3') return <h3 key={index} className="text-2xl md:text-3xl font-bold mt-10 mb-4 text-[#070707] tracking-tight" data-id="element-184">
                        {childrenContent}
                      </h3>;
              if (block.style === 'h4') return <h4 key={index} className="text-xl md:text-2xl font-bold mt-8 mb-4 text-[#070707]" data-id="element-185">
                        {childrenContent}
                      </h4>;
              if (block.style === 'blockquote') return <blockquote key={index} data-id="element-186">{childrenContent}</blockquote>;
              return <p key={index} data-id="element-187">{childrenContent}</p>;
            })}
              </div> : <p data-id="element-188">No content available.</p>}
          </div>
        </div>
      </div>
    </div>;
};